import type { CasingIntervalGeometry, CasingIntervalSegment } from "./types";

export function resolveCasingInteriorDiameterAtDepth(
  depthMDFt: number,
  topCasingInteriorDiameterIn: number,
  topCasingLengthFt: number,
  bottomCasingInteriorDiameterIn: number,
  bottomCasingLengthFt: number
): number {
  const hasTopCasing = topCasingLengthFt > 0 && topCasingInteriorDiameterIn > 0;
  const hasBottomCasing =
    bottomCasingLengthFt > 0 && bottomCasingInteriorDiameterIn > 0;

  if (hasTopCasing && depthMDFt <= topCasingLengthFt) {
    return topCasingInteriorDiameterIn;
  }

  if (hasBottomCasing) {
    return bottomCasingInteriorDiameterIn;
  }

  return topCasingInteriorDiameterIn || bottomCasingInteriorDiameterIn || 0;
}

export function isDepthWithinShroudInterval(
  depthMDFt: number,
  shroudInteriorDiameterIn: number,
  shroudTopMDFt: number,
  shroudBottomMDFt: number
): boolean {
  if (shroudInteriorDiameterIn <= 0) {
    return false;
  }

  return depthMDFt <= shroudTopMDFt && depthMDFt >= shroudBottomMDFt;
}

export function resolveEffectiveEquipmentCasingDiameterAtDepth(
  depthMDFt: number,
  shroudInteriorDiameterIn: number,
  shroudTopMDFt: number,
  shroudBottomMDFt: number,
  topCasingInteriorDiameterIn: number,
  topCasingLengthFt: number,
  bottomCasingInteriorDiameterIn: number,
  bottomCasingLengthFt: number
): number {
  if (
    isDepthWithinShroudInterval(
      depthMDFt,
      shroudInteriorDiameterIn,
      shroudTopMDFt,
      shroudBottomMDFt
    )
  ) {
    return shroudInteriorDiameterIn;
  }

  return resolveCasingInteriorDiameterAtDepth(
    depthMDFt,
    topCasingInteriorDiameterIn,
    topCasingLengthFt,
    bottomCasingInteriorDiameterIn,
    bottomCasingLengthFt
  );
}

export function resolveAnnulusDiameterForInterval(
  startMDFt: number,
  endMDFt: number,
  intervalCasingGeometry: CasingIntervalGeometry
): number | undefined {
  const intervalStartMDFt = Math.min(startMDFt, endMDFt);
  const intervalEndMDFt = Math.max(startMDFt, endMDFt);
  const annulusRanges = intervalCasingGeometry.annulusDiameterRanges ?? [];

  if (annulusRanges.length === 0) {
    return (
      intervalCasingGeometry.annulusDiameterAtStart ??
      intervalCasingGeometry.annulusDiameterAtEnd
    );
  }

  let bestOverlapLengthFt = 0;
  let bestAnnulusDiameterIn: number | undefined;

  for (const annulusRange of annulusRanges) {
    const rangeStartMDFt = Math.min(
      annulusRange.startMDFt,
      annulusRange.endMDFt
    );
    const rangeEndMDFt = Math.max(annulusRange.startMDFt, annulusRange.endMDFt);
    const overlapStartMDFt = Math.max(intervalStartMDFt, rangeStartMDFt);
    const overlapEndMDFt = Math.min(intervalEndMDFt, rangeEndMDFt);
    const overlapLengthFt = overlapEndMDFt - overlapStartMDFt;

    if (overlapLengthFt > bestOverlapLengthFt) {
      bestOverlapLengthFt = overlapLengthFt;
      bestAnnulusDiameterIn = annulusRange.annulusDiameterIn;
    }
  }

  if (bestAnnulusDiameterIn !== undefined) {
    return bestAnnulusDiameterIn;
  }

  return (
    intervalCasingGeometry.annulusDiameterAtStart ??
    intervalCasingGeometry.annulusDiameterAtEnd
  );
}

export function buildCasingIntervalSegments(
  startMDFt: number,
  endMDFt: number,
  intervalCasingGeometry: CasingIntervalGeometry
): CasingIntervalSegment[] {
  const intervalStartMDFt = Math.min(startMDFt, endMDFt);
  const intervalEndMDFt = Math.max(startMDFt, endMDFt);
  if (intervalStartMDFt === intervalEndMDFt) {
    return [];
  }

  const {
    rawCasingInteriorDiameterAtStart,
    rawCasingInteriorDiameterAtEnd,
    effectiveCasingDiameterAtStart,
    effectiveCasingDiameterAtEnd,
    topCasingInteriorDiameterIn,
    topCasingLengthFt,
    bottomCasingInteriorDiameterIn,
    bottomCasingLengthFt
  } = intervalCasingGeometry;

  const hasTopCasing = topCasingLengthFt > 0 && topCasingInteriorDiameterIn > 0;
  const hasBottomCasing =
    bottomCasingLengthFt > 0 && bottomCasingInteriorDiameterIn > 0;
  const crossesCasingBoundary =
    hasTopCasing &&
    hasBottomCasing &&
    rawCasingInteriorDiameterAtStart !== rawCasingInteriorDiameterAtEnd &&
    intervalStartMDFt < topCasingLengthFt &&
    topCasingLengthFt < intervalEndMDFt;

  if (!crossesCasingBoundary) {
    const resolvedIntervalDiameter =
      effectiveCasingDiameterAtStart ||
      effectiveCasingDiameterAtEnd ||
      resolveCasingInteriorDiameterAtDepth(
        intervalEndMDFt,
        topCasingInteriorDiameterIn,
        topCasingLengthFt,
        bottomCasingInteriorDiameterIn,
        bottomCasingLengthFt
      );

    return [
      {
        startMDFt: intervalStartMDFt,
        endMDFt: intervalEndMDFt,
        pipeOuterDiameterIn: resolvedIntervalDiameter,
        annulusDiameterIn: resolveAnnulusDiameterForInterval(
          intervalStartMDFt,
          intervalEndMDFt,
          intervalCasingGeometry
        )
      }
    ];
  }

  const upperIntervalDiameterIn = resolveCasingInteriorDiameterAtDepth(
    intervalStartMDFt,
    topCasingInteriorDiameterIn,
    topCasingLengthFt,
    bottomCasingInteriorDiameterIn,
    bottomCasingLengthFt
  );
  const lowerIntervalDiameterIn = resolveCasingInteriorDiameterAtDepth(
    intervalEndMDFt,
    topCasingInteriorDiameterIn,
    topCasingLengthFt,
    bottomCasingInteriorDiameterIn,
    bottomCasingLengthFt
  );

  return [
    {
      startMDFt: intervalStartMDFt,
      endMDFt: topCasingLengthFt,
      pipeOuterDiameterIn: upperIntervalDiameterIn,
      annulusDiameterIn: resolveAnnulusDiameterForInterval(
        intervalStartMDFt,
        topCasingLengthFt,
        intervalCasingGeometry
      )
    },
    {
      startMDFt: topCasingLengthFt,
      endMDFt: intervalEndMDFt,
      pipeOuterDiameterIn: lowerIntervalDiameterIn,
      annulusDiameterIn: resolveAnnulusDiameterForInterval(
        topCasingLengthFt,
        intervalEndMDFt,
        intervalCasingGeometry
      )
    }
  ].filter(segment => segment.endMDFt > segment.startMDFt);
}
